import React, { useContext, useEffect, useState } from 'react'
import { FormField, FormMultiSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { NameDto } from 'api/Common/Types'
import {
  PaymentProviderGroupDto,
  PSPMethodTypes,
  UpdatePaymentProviderGroupDto,
} from 'api/PaymentProvider/Types/PaymentProviderGroupDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import { AuthSessionContext, isAuthDepartmentsPayments } from '../../../../../utils/AuthContext'

interface PaymentProviderFormValues {
  depositMethods: NameDto<string>[]
  withdrawalMethods: NameDto<string>[]
  uncoveredCardMethods: NameDto<string>[]
  groupName: string
  existing: boolean
}

const EditPaymentProviderGroupFormUI: React.FC<
  FormikProps<PaymentProviderFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, isLoading, errors, isSubmitting, values, setFieldValue, onClose } = props

  const [auth] = useContext(AuthSessionContext)

  const [depositMethodOptions, setDepositMethodOptions] = useState<NameDto<string>[]>()
  const [withdrawMethodOptions, setWithdrawMethodOptions] = useState<NameDto<string>[]>()
  const handleSubmitForm = () => {
    props.setSubmitting(true)
    handleSubmit()
  }

  useEffect(() => {
    if (!props.withdrawalMethodOptions || !props.depositMethodOptions) return
    const filteredWithdrawMethods = props.withdrawalMethodOptions.filter(
      ({ id }) => !values.depositMethods.some((pspOption) => pspOption.id === id)
    )
    const filteredDepositMethods = props.depositMethodOptions.filter(
      ({ id }) => !values.withdrawalMethods.some((pspOption) => pspOption.id === id)
    )
    setDepositMethodOptions(filteredDepositMethods)
    setWithdrawMethodOptions(filteredWithdrawMethods)
  }, [
    values.depositMethods,
    values.withdrawalMethods,
    props.withdrawalMethodOptions,
    props.depositMethodOptions,
  ])

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <Form>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{props.data ? 'Edit PSP Group' : 'Add PSP Group'}</DialogTitle>
          </DialogHeader>

          <FormField
            name='groupName'
            label='PSP Group Name'
            readOnly={!isAuthDepartmentsPayments(auth)}
            error={errors.groupName}
          />

          {props.uncovered ? (
            <FormMultiSelectField
              name='uncoveredCardMethods'
              label='Uncovered Card Deposits'
              selectionSummaryThreshold={5}
              showLoading={isLoading}
              options={props.uncoveredMethodOptions || []}
              value={values.uncoveredCardMethods.map((method) => method.id)}
              error={errors.uncoveredCardMethods as string}
              showSearch
              onChange={(nextValue) => {
                const selectedValues = Array.isArray(nextValue)
                  ? nextValue
                  : nextValue == null
                    ? []
                    : [nextValue]

                setFieldValue(
                  'uncoveredCardMethods',
                  selectedValues
                    .map((id) =>
                      (props.uncoveredMethodOptions || []).find((method) => method.id === id)
                    )
                    .filter((method): method is NameDto<string> => Boolean(method))
                )
              }}
            />
          ) : (
            <>
              <FormMultiSelectField
                name='depositMethods'
                label='PSP Name Deposits'
                showSearch
                showLoading={isLoading}
                selectionSummaryThreshold={5}
                options={
                  depositMethodOptions?.map((method) => ({ name: method.name, id: method.id })) ||
                  []
                }
                value={values.depositMethods.map((method) => method.id)}
                error={errors.depositMethods as string}
                onChange={(nextValue) => {
                  const selectedValues = Array.isArray(nextValue)
                    ? nextValue
                    : nextValue == null
                      ? []
                      : [nextValue]

                  setFieldValue(
                    'depositMethods',
                    selectedValues
                      .map((id) =>
                        (props.depositMethodOptions || []).find((method) => method.id === id)
                      )
                      .filter((method): method is NameDto<string> => Boolean(method))
                  )
                }}
              />
              <FormMultiSelectField
                name='withdrawalMethods'
                label='PSP Name Withdrawals'
                selectionSummaryThreshold={5}
                options={
                  withdrawMethodOptions?.map((method) => ({ name: method.name, id: method.id })) ||
                  []
                }
                value={values.withdrawalMethods.map((method) => method.id)}
                error={errors.withdrawalMethods as string}
                showLoading={isLoading}
                showSearch
                onChange={(nextValue) => {
                  const selectedValues = Array.isArray(nextValue)
                    ? nextValue
                    : nextValue == null
                      ? []
                      : [nextValue]

                  setFieldValue(
                    'withdrawalMethods',
                    selectedValues
                      .map((id) =>
                        (props.withdrawalMethodOptions || []).find((method) => method.id === id)
                      )
                      .filter((method): method is NameDto<string> => Boolean(method))
                  )
                }}
              />
            </>
          )}

          <DialogFooter>
            <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
              Cancel
            </Button>
            <Button disabled={isSubmitting || isLoading} onClick={handleSubmitForm} type='button'>
              Save
              {isLoading && <Spinner />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Form>
    </Dialog>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  data?: PaymentProviderGroupDto
  withdrawalMethodOptions?: NameDto<string>[]
  depositMethodOptions?: NameDto<string>[]
  uncoveredMethodOptions?: NameDto<string>[]
  uncovered?: boolean
  isLoading?: boolean
  onSubmit(values: FormSubmitValues<UpdatePaymentProviderGroupDto>): void
}

const dataToSubmit = (
  values: PaymentProviderFormValues,
  data?: PaymentProviderGroupDto
): FormSubmitValues<UpdatePaymentProviderGroupDto> => {
  const submission = {
    name: values.groupName,
    groupMethods: [
      ...values.depositMethods.map((method) => ({
        paymentProviderId: method.id.toString(),
        methodTypeId: PSPMethodTypes.Deposit,
      })),
      ...values.withdrawalMethods.map((method) => ({
        paymentProviderId: method.id.toString(),
        methodTypeId: PSPMethodTypes.Withdraw,
      })),
      ...values.uncoveredCardMethods.map((method) => ({
        paymentProviderId: method.id.toString(),
        methodTypeId: PSPMethodTypes.Uncovered,
      })),
    ],
  }
  if (data?.methods) {
    return {
      ...submission,
      pspGroupId: data.id,
      pspGroupTypeId: data.pspGroupType.id,
    }
  } else return submission
}

export const EditPaymentProviderGroupForm = withFormik<OuterProps, PaymentProviderFormValues>({
  mapPropsToValues: ({ data: paymentProviderGroup }) => ({
    existing: !!paymentProviderGroup?.methods,
    groupName: paymentProviderGroup?.name ?? '',
    uncoveredCardMethods:
      paymentProviderGroup?.methods
        ?.filter((method) => method.methodType.id === PSPMethodTypes.Uncovered)
        .map((method) => method.paymentProvider) ?? [],
    depositMethods:
      paymentProviderGroup?.methods
        ?.filter((method) => method.methodType.id === PSPMethodTypes.Deposit)
        .map((method) => method.paymentProvider) ?? [],
    withdrawalMethods:
      paymentProviderGroup?.methods
        ?.filter((method) => method.methodType.id === PSPMethodTypes.Withdraw)
        .map((method) => method.paymentProvider) ?? [],
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    await props.onSubmit(dataToSubmit(values, props.data))
    setSubmitting(false)
  },
  validate: (values, props) => {
    const errors: FormikErrors<PaymentProviderFormValues> = {}
    if (!props.uncovered && !values.depositMethods.length) {
      errors.depositMethods = 'You need to select at least one deposit method'
    }
    if (!props.uncovered && !values.withdrawalMethods.length) {
      errors.withdrawalMethods = 'You need to select at least one withdrawal method'
    }
    if (props.uncovered && !values.uncoveredCardMethods.length) {
      errors.uncoveredCardMethods = 'Method cannot be empty'
    }
    if (!values.groupName) {
      errors.groupName = 'Name cannot be empty'
    }
    return errors
  },
})(EditPaymentProviderGroupFormUI)
